import styled from 'styled-components'
import {
  Accordion,
  AccordionItem,
  AccordionToggle,
  AccordionCollapse,
} from '@awaze/design-system.components.accordion'
import {
  Headline,
  HeadlineProps,
  Text,
} from '@awaze/design-system.components.text'
import { Document, INLINES, BLOCKS } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { pxToRems } from '@awaze/design-system.utils.react'
import { Link } from '@awaze/design-system.components.link'
import { Stack } from '@awaze/design-system.components.stack'
import React from 'react'

const isContentfulRichText = (o: unknown): boolean =>
  o &&
  typeof o === 'object' &&
  'nodeType' in o &&
  o.nodeType === BLOCKS.DOCUMENT

const SectionContent = styled.div`
  > p > a + a {
    padding-left: 0;
    &:before {
      content: '|';
      margin-right: ${pxToRems(4)};
    }
  }

  > p > a + span {
    padding-left: 0;
    &:before {
      content: '|';
      margin-left: ${pxToRems(4)};
    }
  }

  > p > span + a {
    padding-left: 0;
    &:before {
      content: '|';
      margin-right: ${pxToRems(4)};
    }
  }
`

const LinkContainer = styled(Stack)`
  *:not(:last-child)::after {
    content: '|';
    margin: 0 4px;
  }
`

const AutoSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`

interface Section {
  id: string
  title: string
  isExpanded?: boolean
}

export interface RichTextSection extends Section {
  content: Document
}

export interface AutoSubSection {
  title: string
  links: {
    slug: string
    name: string
    /** Location that has a child but doens't exist in contentful itself */
    isArtificial?: boolean
  }[]
}

interface AutoSectionContent {
  currentSlug: string
  subSections: AutoSubSection[]
}

export interface AutoSection extends AutoSectionContent {
  id: string
  title: string
  isExpanded?: boolean
}

interface SeoLinkProps {
  slug: string
  name: string
  isArtificial?: boolean
  currentSlug: string
}

const SeoLink: React.FC<SeoLinkProps> = ({
  slug,
  name,
  isArtificial,
  currentSlug,
}) => {
  if (isArtificial) {
    return null
  }
  if (currentSlug === slug) {
    return <span key={slug}>{name}</span>
  }
  return (
    <Link
      key={slug}
      href={
        slug.startsWith('/') || slug.match(/^https?:\/\//) ? slug : `/${slug}`
      }
    >
      {name}
    </Link>
  )
}

const AutoSectionComponent: React.FC<AutoSectionContent> = ({
  currentSlug,
  subSections,
}) => (
  <AutoSectionContainer>
    {subSections.map(({ title, links }) => (
      <React.Fragment key={title}>
        {links.length > 0 && (
          <>
            <Text sizes={300} type="bold">
              {title}
            </Text>
            <LinkContainer
              isHorizontal
              wrap="wrap"
              align="normal"
              direction="row"
              justify="normal"
              spacing={0}
            >
              {links.map(({ slug, name, isArtificial }) => (
                <SeoLink
                  key={slug}
                  slug={slug}
                  name={name}
                  isArtificial={isArtificial}
                  currentSlug={currentSlug}
                />
              ))}
            </LinkContainer>
          </>
        )}
      </React.Fragment>
    ))}
  </AutoSectionContainer>
)

export interface SeoLinksModuleProps {
  headline: HeadlineProps
  sections: (RichTextSection | AutoSection)[]
}

export const SeoLinksModule: React.FC<SeoLinksModuleProps> = ({
  sections,
  headline,
}) => {
  return (
    <>
      <Headline {...headline} />
      <Accordion>
        {sections.map(({ id, title, isExpanded, ...sectionData }) => (
          <AccordionItem key={id} open={isExpanded}>
            <AccordionToggle>{title}</AccordionToggle>
            <AccordionCollapse>
              {'content' in sectionData &&
                isContentfulRichText(sectionData.content) && (
                  <SectionContent>
                    {documentToReactComponents(sectionData.content, {
                      renderNode: {
                        [INLINES.HYPERLINK]: ({ data }, content) => (
                          <Link href={data.uri}>{content}</Link>
                        ),
                        [BLOCKS.PARAGRAPH]: ({ content }, children) => {
                          const index = content.findIndex(
                            (item) =>
                              item.nodeType === 'text' &&
                              !!item.value.trim() &&
                              !item.value.includes('|'),
                          )
                          if (index >= 0) {
                            children[index] = (
                              <span
                                key="seo-links-vertical-bars"
                                className="seo-links-vertical-bars"
                              >
                                {children[index]}
                              </span>
                            )
                          }
                          return <p>{children}</p>
                        },
                      },
                    })}
                  </SectionContent>
                )}
              {'currentSlug' in sectionData && 'subSections' in sectionData && (
                <AutoSectionComponent
                  key={id}
                  {...(sectionData as AutoSectionContent)}
                />
              )}
            </AccordionCollapse>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  )
}
